<template>
  <div class="modal d-block" tabindex="-1" @click.self="$emit('close')">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{
              device ? $t("ecolabels.edit_device") : $t("ecolabels.add_device")
            }}
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="$emit('close')"
          ></button>
        </div>
        <div class="modal-body">
          <!-- AI Autofill Section -->
          <div v-if="!device" class="mb-4 p-3 bg-light rounded">
            <h6 class="mb-2">
              <font-awesome-icon icon="magic" class="me-2" />
              {{ $t("ecolabels.ai_autofill") }}
            </h6>
            <div class="row align-items-end">
              <div class="col-md-8">
                <input
                  type="file"
                  ref="autofillFileInput"
                  class="form-control"
                  accept=".pdf,.doc,.docx"
                  @change="handleAutofillFile"
                />
              </div>
              <div class="col-md-4">
                <button
                  class="btn btn-outline-primary w-100"
                  :disabled="autofillLoading || !autofillFile"
                  @click="runAutofill"
                >
                  <span
                    v-if="autofillLoading"
                    class="spinner-border spinner-border-sm me-1"
                  ></span>
                  {{ $t("ecolabels.autofill_btn") }}
                </button>
              </div>
            </div>
            <small
              v-if="autofillConfidence > 0"
              class="text-muted mt-2 d-block"
            >
              {{
                $t("ecolabels.autofill_confidence", {
                  value: Math.round(autofillConfidence * 100),
                })
              }}
            </small>
          </div>

          <!-- Form -->
          <div v-if="validationErrors.length" class="alert alert-danger py-2">
            <ul class="mb-0 ps-3">
              <li v-for="errorKey in validationErrors" :key="errorKey">
                {{ $t(errorKey) }}
              </li>
            </ul>
          </div>
          <!-- I-REC shortcut: an Evident link / device code relaxes technology and location
               and fills the blanks from the public device register — here, editable. -->
          <div v-if="irecIdentified" class="alert alert-info small py-2">
            <span
              v-if="irecLookupStatus === 'loading'"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ $t(irecHintKey) }}
          </div>
          <form id="ecolabel-device-form" @submit.prevent="handleSubmit">
            <div class="row g-3">
              <!-- Standard -->
              <div class="col-md-6">
                <label class="form-label"
                  >{{ $t("ecolabels.form.standard") }} *</label
                >
                <select v-model="form.standard" class="form-select" required>
                  <option
                    v-for="std in standards"
                    :key="std.value"
                    :value="std.value"
                  >
                    {{ std.label }}
                  </option>
                </select>
              </div>

              <!-- Status -->
              <div class="col-md-6">
                <label class="form-label"
                  >{{ $t("ecolabels.form.status") }} *</label
                >
                <select v-model="form.status" class="form-select" required>
                  <option
                    v-for="st in statuses"
                    :key="st.value"
                    :value="st.value"
                  >
                    {{ st.label }}
                  </option>
                </select>
              </div>

              <!-- Name -->
              <div class="col-md-8">
                <label class="form-label"
                  >{{ $t("ecolabels.form.name") }} *</label
                >
                <!-- Native required only when adding: editing a legacy device with a blank
                     name (e.g. to fix that very name) must not be blocked before handleSubmit. -->
                <input
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  :required="!device"
                />
              </div>

              <!-- Plant Ecolabel ID -->
              <div class="col-md-4">
                <label class="form-label">{{
                  $t("ecolabels.form.plant_id")
                }}</label>
                <input
                  v-model="form.plant_ecolabel_id"
                  type="text"
                  class="form-control"
                  @change="deriveIrecFields"
                />
              </div>

              <!-- Country (searchable; stores the ISO alpha-2 code) -->
              <div class="col-md-4">
                <label class="form-label"
                  >{{ $t("ecolabels.form.country") }} *</label
                >
                <MultiSelectComponent
                  :modelValue="form.country ? [form.country] : []"
                  :options="countryOptions"
                  :placeholder="$t('ecolabels.form.country')"
                  portal
                  hideBulkActions
                  @update:modelValue="
                    (codes: string[]) =>
                      (form.country = codes[codes.length - 1] ?? '')
                  "
                />
              </div>

              <!-- Address -->
              <div class="col-md-8">
                <label class="form-label">{{
                  $t("ecolabels.form.address")
                }}</label>
                <input
                  v-model="form.address"
                  type="text"
                  class="form-control"
                />
                <small class="text-muted">
                  {{ $t("ecolabels.form.location_hint") }}
                </small>
              </div>

              <!-- Latitude -->
              <div class="col-md-6">
                <label class="form-label">{{
                  $t("ecolabels.form.latitude")
                }}</label>
                <input
                  v-model="form.latitude"
                  type="text"
                  inputmode="decimal"
                  class="form-control"
                />
              </div>

              <!-- Longitude -->
              <div class="col-md-6">
                <label class="form-label">{{
                  $t("ecolabels.form.longitude")
                }}</label>
                <input
                  v-model="form.longitude"
                  type="text"
                  inputmode="decimal"
                  class="form-control"
                />
              </div>

              <!-- Renewable Type -->
              <div class="col-md-6">
                <label class="form-label"
                  >{{ $t("ecolabels.form.renewable_type") }} *</label
                >
                <!-- Canonical vocabulary — the backend validators reject anything else -->
                <select v-model="form.renewable_type" class="form-select">
                  <option :value="null">-</option>
                  <option
                    v-for="type in RENEWABLE_TYPES"
                    :key="type"
                    :value="type"
                  >
                    {{ $t(`ecolabels.renewable_types.${type.toLowerCase()}`) }}
                  </option>
                </select>
              </div>

              <!-- Technology Code -->
              <div class="col-md-6">
                <label class="form-label">{{
                  $t("ecolabels.form.technology_code")
                }}</label>
                <input
                  v-model="form.technology_code"
                  type="text"
                  class="form-control"
                  :placeholder="
                    $t('ecolabels.form.technology_code_placeholder')
                  "
                />
              </div>

              <!-- Registration Authority (canonical vocabulary, same as the backend) -->
              <div class="col-md-6">
                <label class="form-label"
                  >{{ $t("ecolabels.form.registration_authority")
                  }}<template v-if="form.plant_ecolabel_id?.trim()">
                    *</template
                  ></label
                >
                <select
                  v-model="form.registration_authority"
                  class="form-select"
                  @change="deriveIrecFields"
                >
                  <option :value="null">-</option>
                  <option
                    v-for="authority in REGISTRATION_AUTHORITIES"
                    :key="authority"
                    :value="authority"
                  >
                    {{ REGISTRATION_AUTHORITY_LABELS[authority] ?? authority }}
                  </option>
                </select>
              </div>

              <!-- Relevant Link -->
              <div class="col-md-6">
                <label class="form-label">{{
                  $t("ecolabels.form.relevant_link")
                }}</label>
                <input
                  v-model="form.relevant_link"
                  type="url"
                  class="form-control"
                  @change="deriveIrecFields"
                />
              </div>

              <!-- Capacity -->
              <div class="col-md-4">
                <label class="form-label">{{
                  $t("ecolabels.form.capacity")
                }}</label>
                <input
                  v-model="form.capacity"
                  type="text"
                  inputmode="decimal"
                  class="form-control"
                />
              </div>

              <!-- Capacity Units -->
              <div class="col-md-2">
                <label class="form-label">{{
                  $t("ecolabels.form.capacity_units")
                }}</label>
                <select v-model="form.capacity_units" class="form-select">
                  <option value="MW">MW</option>
                  <option value="kW">kW</option>
                  <option value="GW">GW</option>
                </select>
              </div>

              <!-- COD -->
              <div class="col-md-4">
                <label class="form-label">{{ $t("ecolabels.form.cod") }}</label>
                <input v-model="form.cod" type="date" class="form-control" />
              </div>

              <!-- Date Reviewed -->
              <div class="col-md-4">
                <label class="form-label">{{
                  $t("ecolabels.form.date_reviewed")
                }}</label>
                <input
                  v-model="form.date_reviewed"
                  type="date"
                  class="form-control"
                />
              </div>

              <!-- Validity window (relevant for hydro/bioenergy) -->
              <div class="col-md-4">
                <label class="form-label">{{
                  $t("ecolabels.form.valid_from")
                }}</label>
                <input
                  v-model="form.valid_from"
                  type="date"
                  class="form-control"
                />
              </div>

              <div class="col-md-4">
                <label class="form-label">{{
                  $t("ecolabels.form.valid_through")
                }}</label>
                <input
                  v-model="form.valid_through"
                  type="date"
                  class="form-control"
                />
              </div>

              <!-- Rejection Reason: mailed to the submitter in the daily review summary -->
              <div v-if="isRejectedEdit" class="col-12">
                <label class="form-label" for="rejectionReason"
                  >{{ $t("ecolabels.form.rejection_reason")
                  }}{{ rejectionReasonRequired ? " *" : "" }}</label
                >
                <textarea
                  id="rejectionReason"
                  v-model="rejectionReason"
                  class="form-control"
                  :class="{ 'is-invalid': rejectionReasonInvalid }"
                  rows="2"
                  :required="rejectionReasonRequired"
                  maxlength="2000"
                  :aria-invalid="rejectionReasonInvalid"
                  :aria-describedby="
                    rejectionReasonInvalid ? 'rejectionReasonError' : undefined
                  "
                  :placeholder="
                    $t('ecolabels.submissions.rejection_reason_placeholder')
                  "
                ></textarea>
                <div
                  v-if="rejectionReasonInvalid"
                  id="rejectionReasonError"
                  class="invalid-feedback"
                >
                  {{ $t("ecolabels.form.errors.rejection_reason_required") }}
                </div>
              </div>

              <!-- Public Notes -->
              <div class="col-12">
                <label class="form-label">{{
                  $t("ecolabels.form.public_notes")
                }}</label>
                <textarea
                  v-model="form.public_notes"
                  class="form-control"
                  rows="3"
                ></textarea>
              </div>

              <!-- Registry Device ID -->
              <div class="col-md-6">
                <label class="form-label">{{
                  $t("ecolabels.form.registry_device_id")
                }}</label>
                <input
                  v-model.number="form.registry_device_id"
                  type="number"
                  class="form-control"
                />
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="$emit('close')"
          >
            {{ $t("common.cancel") }}
          </button>
          <button
            type="submit"
            form="ecolabel-device-form"
            class="btn btn-primary"
            :disabled="saving"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            {{ $t("common.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
</template>

<script setup lang="ts">
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { without } from "lodash-es";
import { computed, ref, watch } from "vue";
import { ecolabelsApi } from "../api/ecolabelsApi";
import { isoCountryOptions, withUnknownCodes } from "../countryOptions";
import {
  applyIrecIdentity,
  hasIrecIdentity,
  invalidNumberFields,
  isValidLatitude,
  isValidLongitude,
  NUMBER_FIELD_ERROR_KEYS,
  toFiniteNumber,
  type IrecLookupStatus,
} from "../upload/deviceUploadMapping";
import { irecLookupHintKey, lookupAndFillIrecRow } from "../upload/irecLookup";
import type {
  EcolabelDevice,
  EcolabelDeviceCreateRequest,
  FilterOption,
} from "../api/ecolabelsTypes";
import {
  EcolabelStandard,
  EcolabelStatus,
  REGISTRATION_AUTHORITIES,
  REGISTRATION_AUTHORITY_LABELS,
  RENEWABLE_TYPES,
} from "../api/ecolabelsTypes";

const props = defineProps<{
  device: EcolabelDevice | null;
  standards: FilterOption[];
  statuses: FilterOption[];
  companyId: number;
}>();

const emit = defineEmits<{
  close: [];
  saved: [];
}>();

const { globalErrorHandler } = useGlobalHandlers();

// Form state
const getDefaultForm = (): EcolabelDeviceCreateRequest => ({
  standard: EcolabelStandard.EKOENERGY,
  name: "",
  country: "",
  address: null,
  latitude: null,
  longitude: null,
  renewable_type: null,
  technology_code: null,
  registration_authority: null,
  relevant_link: null,
  capacity: null,
  capacity_units: "MW",
  cod: null,
  status: EcolabelStatus.NOT_APPROVED,
  public_notes: null,
  plant_ecolabel_id: null,
  registry_device_id: null,
  date_reviewed: null,
  valid_from: null,
  valid_through: null,
});
const form = ref<EcolabelDeviceCreateRequest>(getDefaultForm());

// Not in `form`: the create payload has no rejection reason, so only edits show the field.
const rejectionReason = ref("");
const isRejectedEdit = computed(
  () => Boolean(props.device) && form.value.status === EcolabelStatus.REJECTED
);
// Only a change into REJECTED needs a new reason; an already-rejected device keeps its stored one.
const rejectionReasonRequired = computed(
  () => isRejectedEdit.value && props.device?.status !== EcolabelStatus.REJECTED
);
const REJECTION_REASON_REQUIRED_ERROR =
  "ecolabels.form.errors.rejection_reason_required";
// Set by a failed save, so the field is not flagged while the admin is still typing.
const rejectionReasonSubmitted = ref(false);
const rejectionReasonInvalid = computed(
  () => rejectionReasonSubmitted.value && rejectionReasonErrors().length > 0
);

// Full ISO country list; legacy devices can carry codes outside it (regions like
// BE-FED, or the XX sentinel), which get an ad-hoc option so editing still shows them.
const countryOptions = computed(() =>
  withUnknownCodes(isoCountryOptions(), [form.value.country])
);

const saving = ref(false);
const autofillLoading = ref(false);
const autofillFile = ref<File | null>(null);
const autofillConfidence = ref(0);

// EKOenergy requires that a device is identifiable before it can be saved: what it is
// (renewable type) and where it is (address or coordinates). A registration ID is only
// meaningful together with the authority that issued it. Mirrored server-side in
// EcolabelDeviceManualCreateRequest. Holds i18n keys, translated in the template.
const validationErrors = ref<string[]>([]);

// Initialize form from device
watch(
  () => props.device,
  (device) => {
    if (device) {
      form.value = {
        standard: device.standard,
        name: device.name,
        country: device.country,
        address: device.address,
        latitude: device.latitude,
        longitude: device.longitude,
        renewable_type: device.renewable_type,
        technology_code: device.technology_code,
        registration_authority: device.registration_authority,
        relevant_link: device.relevant_link,
        capacity: device.capacity,
        capacity_units: device.capacity_units,
        cod: device.cod,
        status: device.status,
        public_notes: device.public_notes,
        plant_ecolabel_id: device.plant_ecolabel_id,
        registry_device_id: device.registry_device_id,
        date_reviewed: device.date_reviewed,
        valid_from: device.valid_from,
        valid_through: device.valid_through,
      };
      rejectionReason.value =
        device.status === EcolabelStatus.REJECTED
          ? (device.rejection_reason ?? "")
          : "";
      rejectionReasonSubmitted.value = false;
      return;
    }

    form.value = getDefaultForm();
    rejectionReason.value = "";
    rejectionReasonSubmitted.value = false;
  },
  { immediate: true }
);

// A new status is a new decision: clear the previous save's reason error, here and in the alert.
watch(
  () => form.value.status,
  () => {
    rejectionReasonSubmitted.value = false;
    validationErrors.value = without(
      validationErrors.value,
      REJECTION_REASON_REQUIRED_ERROR
    );
  }
);

const handleAutofillFile = (event: Event) => {
  const target = event.target as HTMLInputElement;
  autofillFile.value = target.files?.[0] ?? null;
};

const runAutofill = async () => {
  if (!autofillFile.value) return;

  autofillLoading.value = true;
  try {
    const response = await ecolabelsApi.autofillDevice(
      props.companyId,
      autofillFile.value,
      form.value.standard
    );
    const { suggested_device, confidence } = response.data.data;

    // Merge suggested values into form, keeping empty values where no suggestion
    if (suggested_device) {
      Object.entries(suggested_device).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          (form.value as Record<string, unknown>)[key] = value;
        }
      });
    }

    autofillConfidence.value = confidence;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    autofillLoading.value = false;
  }
};

// An I-REC-identified device (Evident link, or device code plus the IREC registry)
// waives technology and location — the reviewer verifies them on Evident. Mirrors
// EcolabelDeviceManualCreateRequest.
const irecIdentified = computed(() =>
  hasIrecIdentity({
    relevant_link: form.value.relevant_link ?? null,
    registration_authority: form.value.registration_authority ?? null,
    plant_ecolabel_id: form.value.plant_ecolabel_id ?? null,
  })
);

const irecLookupStatus = ref<IrecLookupStatus | null>(null);
const irecHintKey = computed(() => irecLookupHintKey(irecLookupStatus.value));

/** Live I-REC shortcut: derive the code/registry/link, then fill the blanks from the
 * public device register so the admin sees every value before saving. */
const deriveIrecFields = () => {
  void lookupAndFillIrecRow(form.value, (status) => {
    irecLookupStatus.value = status;
  });
};

const validate = (): boolean => {
  // Derive once more right before validating, so a code committed and saved in one
  // motion can't slip through with blank identity fields.
  applyIrecIdentity(form.value);
  const errors: string[] = [];
  // Name and country are required in every case — the register lookup normally fills
  // them for I-REC devices, but nothing is invented when it can't. The native
  // `required` lets whitespace-only values through.
  if (!form.value.name.trim()) {
    errors.push("ecolabels.form.errors.name_required");
  }
  if (!form.value.country.trim()) {
    errors.push("ecolabels.form.errors.country_required");
  }
  if (!form.value.renewable_type && !irecIdentified.value) {
    errors.push("ecolabels.form.errors.renewable_type_required");
  }
  errors.push(...numberFieldErrors());
  if (
    !form.value.address?.trim() &&
    !(
      isValidLatitude(form.value.latitude) &&
      isValidLongitude(form.value.longitude)
    ) &&
    !irecIdentified.value
  ) {
    errors.push("ecolabels.form.errors.location_required");
  }
  if (
    form.value.plant_ecolabel_id?.trim() &&
    !form.value.registration_authority &&
    !irecIdentified.value
  ) {
    errors.push("ecolabels.form.errors.registration_authority_required");
  }
  validationErrors.value = errors;
  return errors.length === 0;
};

const handleSubmit = async () => {
  // Mandatory fields apply when ADDING a plant; editing legacy devices with
  // incomplete data (e.g. fixing a name) must stay possible.
  if (props.device) {
    rejectionReasonSubmitted.value = true;
    validationErrors.value = [
      ...numberFieldErrors(),
      ...rejectionReasonErrors(),
    ];
    if (validationErrors.value.length > 0) return;
  } else if (!validate()) return;
  saving.value = true;
  try {
    const payload = {
      ...form.value,
      latitude: toFiniteNumber(form.value.latitude),
      longitude: toFiniteNumber(form.value.longitude),
      capacity: toFiniteNumber(form.value.capacity),
      registry_device_id:
        typeof form.value.registry_device_id === "number"
          ? form.value.registry_device_id
          : null,
    };
    if (props.device) {
      await ecolabelsApi.updateDevice(props.device.id, {
        ...payload,
        // `null`, not "": the backend refuses a blank reason, and null keeps the stored one.
        rejection_reason: isRejectedEdit.value
          ? rejectionReason.value.trim() || null
          : null,
      });
    } else {
      await ecolabelsApi.createDevice(payload);
    }
    emit("saved");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    saving.value = false;
  }
};

function numberFieldErrors(): string[] {
  return invalidNumberFields(form.value).map(
    (field) => NUMBER_FIELD_ERROR_KEYS[field]
  );
}

function rejectionReasonErrors(): string[] {
  if (!rejectionReasonRequired.value || rejectionReason.value.trim()) return [];
  return [REJECTION_REASON_REQUIRED_ERROR];
}
</script>
